import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { resolveImageUrl } from '../lib/imageUrl';
import { ShoppingCart, Plus, Minus, Trash2 } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useCart } from '../contexts/CartContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ShopPage() {
  const { t, getLocalizedContent } = useLanguage();
  const { addItem } = useCart();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('all');

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = ['all', ...new Set(products.map(p => p.category))];
  const filteredProducts = category === 'all' 
    ? products 
    : products.filter(p => p.category === category);

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div data-testid="shop-page">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial="hidden" animate="visible" variants={fadeInUp}>
            <span className="inline-block px-4 py-1 bg-[#0B6AB3]/20 text-[#0B6AB3] text-sm font-medium rounded-full mb-4">
              Boutique
            </span>
            <h1 className="text-4xl lg:text-5xl font-bold text-white font-['Montserrat']">
              {t('products_title')}
            </h1>
            <p className="text-gray-300 mt-4 max-w-2xl mx-auto text-lg">
              {t('products_subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-2 mb-12">
            {categories.map((cat) => (
              <Button
                key={cat}
                variant={category === cat ? 'default' : 'outline'}
                className={category === cat ? 'bg-[#0B6AB3] text-white' : 'border-gray-300'}
                onClick={() => setCategory(cat)}
                data-testid={`category-${cat}`}
              >
                {cat === 'all' ? t('common_all') : cat.charAt(0).toUpperCase() + cat.slice(1)}
              </Button>
            ))}
          </div>

          {/* Products Grid */}
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0B6AB3]"></div>
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-20 text-gray-500">
              Aucun produit disponible
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredProducts.map((product, index) => (
                <motion.div
                  key={product.id}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeInUp}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card className="h-full border-0 shadow-sm hover:shadow-lg transition-all duration-300 group" data-testid={`product-card-${index}`}>
                    <div className="relative h-48 overflow-hidden rounded-t-lg bg-gray-100">
                      <img
                        src={resolveImageUrl(product.images?.[0]) || 'https://via.placeholder.com/300x200'}
                        alt={getLocalizedContent(product.name)}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      {!product.in_stock && (
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                          <Badge variant="destructive">{t('products_out_of_stock')}</Badge>
                        </div>
                      )}
                      {product.is_featured && (
                        <Badge className="absolute top-2 left-2 bg-[#E50E24]">
                          Populaire
                        </Badge>
                      )}
                    </div>
                    <CardContent className="p-4">
                      <Badge variant="outline" className="mb-2 text-xs">
                        {product.category}
                      </Badge>
                      <h3 className="font-semibold text-[#0F172A] mb-2 line-clamp-2">
                        {getLocalizedContent(product.name)}
                      </h3>
                      <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                        {getLocalizedContent(product.description)}
                      </p>
                      <div className="flex items-center justify-between pt-3 border-t">
                        <span className="text-xl font-bold text-[#0B6AB3]">
                          {product.price?.toLocaleString()} <span className="text-sm font-normal">{t('common_fcfa')}</span>
                        </span>
                        <Button
                          size="sm"
                          className="bg-[#0B6AB3] hover:bg-[#095c9e] text-white"
                          disabled={!product.in_stock}
                          onClick={() => addItem(product)}
                          data-testid={`add-to-cart-${index}`}
                        >
                          <ShoppingCart className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Info Banner */}
      <section className="py-12 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="w-12 h-12 bg-[#0B6AB3]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <ShoppingCart className="h-6 w-6 text-[#0B6AB3]" />
              </div>
              <h4 className="font-semibold text-[#0F172A]">Paiement à la livraison</h4>
              <p className="text-sm text-gray-600 mt-1">Payez à la réception de votre commande</p>
            </div>
            <div>
              <div className="w-12 h-12 bg-[#0B6AB3]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg className="h-6 w-6 text-[#0B6AB3]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h4 className="font-semibold text-[#0F172A]">Produits de qualité</h4>
              <p className="text-sm text-gray-600 mt-1">Équipements certifiés et garantis</p>
            </div>
            <div>
              <div className="w-12 h-12 bg-[#0B6AB3]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg className="h-6 w-6 text-[#0B6AB3]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <h4 className="font-semibold text-[#0F172A]">Support client</h4>
              <p className="text-sm text-gray-600 mt-1">Service après-vente disponible</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
